import { MESSAGE_TYPE } from 'shared/constants/messages';
import { applyPageFilters, sortComparator } from './helpers';
import filterQueryGenerator from 'dashboard/helper/filterQueryGenerator';
import camelcaseKeys from 'camelcase-keys';

export const getSelectedChatConversation = ({
  allConversations,
  selectedChatId,
}) =>
  allConversations.filter(conversation => conversation.id === selectedChatId);

const isInternalInboxConversation = (conversation, getInbox) => {
  const inbox = getInbox?.(conversation.inbox_id) || {};
  return (
    inbox.channel_type === 'Channel::Internal' ||
    conversation?.meta?.channel === 'Channel::Internal' ||
    conversation?.meta?.inbox?.channel_type === 'Channel::Internal' ||
    !!conversation?.additional_attributes?.service_ticket_id
  );
};

const isInternalDirectMessageConversation = conversation => {
  return (
    conversation?.additional_attributes?.internal_conversation_type ===
    'direct_message'
  );
};

const normalizeInboxMemberIds = inbox => {
  const memberIds = inbox?.member_ids || inbox?.memberIds || [];
  return Array.isArray(memberIds) ? memberIds.map(id => Number(id)) : [];
};

const isServiceDeskConversation = conversation =>
  !!conversation?.additional_attributes?.service_ticket_id;

const participantIdsIncludeUser = (conversation, currentUserID) => {
  const participantIds =
    conversation?.additional_attributes?.internal_participant_ids || [];
  return Array.isArray(participantIds)
    ? participantIds.map(id => Number(id)).includes(Number(currentUserID))
    : false;
};

const isInternalConversationMine = (conversation, getInbox, currentUserID) => {
  if (isInternalDirectMessageConversation(conversation)) {
    return participantIdsIncludeUser(conversation, currentUserID);
  }

  if (isServiceDeskConversation(conversation)) {
    if (participantIdsIncludeUser(conversation, currentUserID)) {
      return true;
    }
  }

  const inbox = getInbox?.(conversation.inbox_id) || {};
  return normalizeInboxMemberIds(inbox).includes(Number(currentUserID));
};

const getters = {
  getAllConversations: ({ allConversations, chatSortFilter: sortKey }) => {
    return allConversations.sort((a, b) => sortComparator(a, b, sortKey));
  },
  getSelectedChat: ({ selectedChatId, allConversations }) => {
    const selectedChat = allConversations.find(
      conversation => conversation.id === selectedChatId
    );
    return selectedChat || {};
  },
  getSelectedChatAttachments: ({ selectedChatId, attachments }) => {
    return attachments[selectedChatId] || [];
  },
  getChatListFilters: ({ conversationFilters }) => conversationFilters,
  getFilteredConversationsCount: ({ filteredConversationsCount }) =>
    filteredConversationsCount,
  getLastEmailInSelectedChat: (stage, _getters) => {
    const selectedChat = _getters.getSelectedChat;
    const { messages = [] } = selectedChat;
    const lastEmail = [...messages].reverse().find(message => {
      const {
        content_attributes: contentAttributes = {},
        message_type: messageType,
      } = message;
      const { email = {} } = contentAttributes;
      const isIncomingOrOutgoing =
        messageType === MESSAGE_TYPE.OUTGOING ||
        messageType === MESSAGE_TYPE.INCOMING;
      if (email.from && isIncomingOrOutgoing) {
        return true;
      }
      return false;
    });

    return lastEmail;
  },
  getMineChats: (_state, _, __, rootGetters) => activeFilters => {
    const currentUserID = rootGetters.getCurrentUser?.id;
    const getInbox = rootGetters['inboxes/getInbox'];

    return _state.allConversations.filter(conversation => {
      const shouldFilter = applyPageFilters(conversation, activeFilters);
      if (!shouldFilter) {
        return false;
      }

      if (isServiceDeskConversation(conversation)) {
        return participantIdsIncludeUser(conversation, currentUserID);
      }

      if (isInternalInboxConversation(conversation, getInbox)) {
        return isInternalConversationMine(
          conversation,
          getInbox,
          currentUserID
        );
      }

      const { assignee } = conversation.meta;
      const isAssignedToMe =
        assignee && Number(assignee.id) === Number(currentUserID);
      const isChatMine = isAssignedToMe && shouldFilter;

      return isChatMine;
    });
  },
  getAppliedConversationFiltersV2: _state => {
    // TODO: Replace existing one with V2 after migrating the filters to use camelcase
    return _state.appliedFilters.map(camelcaseKeys);
  },
  getAppliedConversationFilters: _state => {
    return _state.appliedFilters;
  },
  getAppliedConversationFiltersQuery: _state => {
    const hasAppliedFilters = _state.appliedFilters.length !== 0;
    return hasAppliedFilters ? filterQueryGenerator(_state.appliedFilters) : [];
  },
  getUnAssignedChats: (_state, _, __, rootGetters) => activeFilters => {
    const currentUserID = rootGetters.getCurrentUser?.id;
    const getInbox = rootGetters['inboxes/getInbox'];

    return _state.allConversations.filter(conversation => {
      const shouldFilter = applyPageFilters(conversation, activeFilters);
      if (!shouldFilter) {
        return false;
      }

      if (isServiceDeskConversation(conversation)) {
        return !participantIdsIncludeUser(conversation, currentUserID);
      }

      if (isInternalInboxConversation(conversation, getInbox)) {
        return !isInternalConversationMine(
          conversation,
          getInbox,
          currentUserID
        );
      }

      const isUnAssigned = !conversation.meta.assignee;
      return isUnAssigned && shouldFilter;
    });
  },
  getAllStatusChats: _state => activeFilters => {
    return _state.allConversations.filter(conversation => {
      const shouldFilter = applyPageFilters(conversation, activeFilters);
      return shouldFilter;
    });
  },
  getChatListLoadingStatus: ({ listLoadingStatus }) => listLoadingStatus,
  getAllMessagesLoaded(_state) {
    const [chat] = getSelectedChatConversation(_state);
    return !chat || chat.allMessagesLoaded === undefined
      ? false
      : chat.allMessagesLoaded;
  },
  getUnreadCount(_state) {
    const [chat] = getSelectedChatConversation(_state);
    if (!chat) return [];
    return chat.messages.filter(
      chatMessage =>
        chatMessage.created_at * 1000 > chat.agent_last_seen_at * 1000 &&
        chatMessage.message_type === 0 &&
        chatMessage.private !== true
    ).length;
  },
  getChatStatusFilter: ({ chatStatusFilter }) => chatStatusFilter,
  getChatSortFilter: ({ chatSortFilter }) => chatSortFilter,
  getSelectedInbox: ({ currentInbox }) => currentInbox,
  getConversationById: _state => conversationId => {
    return _state.allConversations.find(
      value => value.id === Number(conversationId)
    );
  },
  getConversationParticipants: _state => {
    return _state.conversationParticipants;
  },
  getConversationLastSeen: _state => {
    return _state.conversationLastSeen;
  },

  getContextMenuChatId: _state => {
    return _state.contextMenuChatId;
  },
};

export default getters;
